/**
 * Preset Application Logic
 *
 * Applies animation preset choreography to page elements by:
 * 1. Detecting element roles via heuristics
 * 2. Matching roles to preset rules
 * 3. Building cssAnimation arrays for each matched element
 * 4. Returning element updates ready for ActionBarOps consumption
 */

import { detectAllRoles, sortByStaggerDirection } from "./roleDetection";
import {
  AnimationPreset,
  DetectedRole,
  ElementRole,
  PageContext,
  PresetAnimation,
  PresetRule,
  RoleDetectionElement,
  StaggerDirection
} from "./types";

/** Shape of a single CSS animation entry on an element */
interface CssAnimationEntry {
  stage: string;
  name: string;
  direction: string;
  delay: number;
  transition: string;
  textType: string;
  isPreview: boolean;
  duration: number | null;
}

/** Element updates keyed by uniqueId, ready for ActionBarOps */
export interface ElementUpdates {
  [uniqueId: string]: {
    uniqueId: string;
    pageId: string;
    cssAnimation: CssAnimationEntry[];
  };
}

/** Result of applying a preset to a single page */
export interface PresetApplicationResult {
  elementUpdates: ElementUpdates;
  pageId: string;
}

/** Minimal page shape needed for preset application */
export interface PageData {
  id: string;
  elementsOrder: string[];
  duration?: number;
}

/** Minimal design data shape needed for preset application */
export interface DesignData {
  elements: Record<string, RoleDetectionElement & { pageId: string }>;
  pages: Record<string, PageData>;
  pagesOrder: string[];
  width: number;
  height: number;
}

/**
 * Clear all animations from the given element IDs.
 * Returns element updates that set cssAnimation to an empty array.
 */
export const clearPageAnimations = (
  elementIds: string[],
  elements: Record<string, { pageId: string }>,
  pageId: string
): ElementUpdates => {
  const updates: ElementUpdates = {};

  for (const id of elementIds) {
    const element = elements[id];
    if (element && element.pageId === pageId) {
      updates[id] = {
        uniqueId: id,
        pageId,
        cssAnimation: []
      };
    }
  }

  return updates;
};

/**
 * Build a cssAnimation array from a preset rule's animation definitions.
 * Applies stagger delay offset based on the element's stagger index.
 */
export const buildCssAnimationArray = (
  animations: PresetAnimation[],
  staggerIndex: number,
  staggerOffset: number
): CssAnimationEntry[] =>
  animations.map(anim => ({
    stage: anim.stage,
    name: anim.name,
    direction: anim.direction,
    delay: anim.delay + calculateStaggerDelay(staggerOffset, staggerIndex),
    transition: anim.transition,
    textType: anim.textType ?? "",
    isPreview: false,
    duration: anim.duration > 0 ? anim.duration : null
  }));

/**
 * Calculate the stagger delay for an element based on its index
 * among same-role elements.
 */
export const calculateStaggerDelay = (
  staggerOffset: number,
  index: number
): number => staggerOffset * index;

/**
 * Find the preset rule that matches a given element role.
 */
const findRuleForRole = (
  preset: AnimationPreset,
  role: ElementRole
): PresetRule | undefined => preset.rules.find(rule => rule.role === role);

/**
 * Get elements that share the same role, sorted by stagger direction.
 */
const getSameRoleElements = (
  role: ElementRole,
  detectedRoles: DetectedRole[],
  allElements: Record<string, RoleDetectionElement>,
  staggerDirection: StaggerDirection,
  pageContext?: PageContext
): RoleDetectionElement[] => {
  const sameRoleIds = detectedRoles
    .filter(r => r.role === role)
    .map(r => r.uniqueId);

  const sameRoleElements = sameRoleIds
    .map(id => allElements[id])
    .filter((el): el is RoleDetectionElement => el !== undefined);

  return sortByStaggerDirection(
    sameRoleElements,
    staggerDirection,
    pageContext
  );
};

/**
 * Get all animatable leaf elements from a group recursively.
 * For nested groups, descends until reaching non-group elements.
 */
const getGroupChildren = (
  groupId: string,
  elements: Record<string, RoleDetectionElement & { pageId: string }>,
  pageId: string
): string[] => {
  const group = elements[groupId];
  if (!group || group.type !== "group" || !group.elementsOrder?.length) {
    return [];
  }

  const children: string[] = [];
  for (const childId of group.elementsOrder) {
    const child = elements[childId];
    if (!child || child.pageId !== pageId) continue;

    if (child.type === "group") {
      children.push(...getGroupChildren(childId, elements, pageId));
    } else {
      children.push(childId);
    }
  }
  return children;
};

/**
 * Apply a preset to a single page's elements.
 *
 * Algorithm:
 * 1. Gather page elements from design data
 * 2. Run role detection to classify elements
 * 3. For each detected role, find the matching preset rule
 * 4. Build cssAnimation arrays with stagger offsets
 * 5. Return element updates for all matched + unmatched elements
 *
 * Unmatched elements (those on the page but not matching any rule)
 * get their animations cleared (replace-all behavior).
 */
export const applyPresetToPage = (
  preset: AnimationPreset,
  pageId: string,
  designData: DesignData
): PresetApplicationResult => {
  const page = designData.pages[pageId];
  if (!page) {
    return { elementUpdates: {}, pageId };
  }

  const pageContext: PageContext = {
    pageWidth: designData.width,
    pageHeight: designData.height
  };

  // Gather page elements
  const pageElements: RoleDetectionElement[] = page.elementsOrder
    .map(id => designData.elements[id])
    .filter((el): el is RoleDetectionElement & { pageId: string } => !!el);

  // Start with clearing all page element animations (replace-all behavior)
  // Also clears children of groups so nested elements get initialized
  const elementUpdates: ElementUpdates = {};
  const clearElement = (id: string) => {
    const element = designData.elements[id];
    if (!element) return;
    elementUpdates[id] = {
      uniqueId: id,
      pageId,
      cssAnimation: []
    };
    // Recursively clear group children
    if (element.type === "group" && element.elementsOrder?.length) {
      for (const childId of element.elementsOrder) {
        clearElement(childId);
      }
    }
  };
  for (const id of page.elementsOrder) {
    clearElement(id);
  }

  // Detect roles
  const detectedRoles = detectAllRoles(
    pageElements,
    designData.elements,
    pageContext
  );

  if (detectedRoles.length === 0) {
    return { elementUpdates, pageId };
  }

  // Cache stagger-sorted elements per role to avoid redundant sorting
  const staggerCache = new Map<ElementRole, RoleDetectionElement[]>();

  const getCachedSameRoleElements = (
    role: ElementRole,
    staggerDirection: StaggerDirection
  ): RoleDetectionElement[] => {
    const cached = staggerCache.get(role);
    if (cached) return cached;

    const sorted = getSameRoleElements(
      role,
      detectedRoles,
      designData.elements,
      staggerDirection,
      pageContext
    );
    staggerCache.set(role, sorted);
    return sorted;
  };

  for (const detected of detectedRoles) {
    const rule = findRuleForRole(preset, detected.role);
    if (!rule || rule.animations.length === 0) continue;

    const staggerDirection = rule.staggerDirection ?? "ttb-ltr";
    const staggerOffset = rule.staggerOffset ?? 0;

    const sameRoleElements = getCachedSameRoleElements(
      detected.role,
      staggerDirection
    );

    let staggerIndex = sameRoleElements.findIndex(
      el => el.uniqueId === detected.uniqueId
    );
    if (staggerIndex === -1) staggerIndex = 0;

    const cssAnimation = buildCssAnimationArray(
      rule.animations,
      staggerIndex,
      staggerOffset
    );

    // If the detected element is a group, apply animation to its children
    const detectedElement = designData.elements[detected.uniqueId];
    if (detectedElement?.type === "group") {
      const childIds = getGroupChildren(
        detected.uniqueId,
        designData.elements,
        pageId
      );
      for (const childId of childIds) {
        elementUpdates[childId] = {
          uniqueId: childId,
          pageId,
          cssAnimation
        };
      }
      // Ensure group itself has empty animation
      elementUpdates[detected.uniqueId] = {
        uniqueId: detected.uniqueId,
        pageId,
        cssAnimation: []
      };
    } else {
      elementUpdates[detected.uniqueId] = {
        uniqueId: detected.uniqueId,
        pageId,
        cssAnimation
      };
    }
  }

  return { elementUpdates, pageId };
};

/**
 * Apply a preset to all pages in a design.
 * Each page gets independent role detection.
 */
export const applyPresetToAllPages = (
  preset: AnimationPreset,
  designData: DesignData
): PresetApplicationResult[] =>
  designData.pagesOrder.map(pageId =>
    applyPresetToPage(preset, pageId, designData)
  );
